import React, { useState } from 'react';
import { Form, Icon as LegacyIcon } from '@ant-design/compatible';
import '@ant-design/compatible/assets/index.css';
import {
  Button,
  Input,
  message,
  Modal,
  Popconfirm,
  Skeleton,
  Switch,
} from 'antd';
import { FormComponentProps } from '@ant-design/compatible/lib/form';
import firebase from 'firebase';
import { getClient } from '../../apollo';
import {
  Client,
  useDeleteClientMutation,
  useGetClientBasicInfoQuery,
  useUpdateClientMutation,
} from '../../graphql/datamodel.gen';
import { sanitizeObject } from '../../utils/object';

interface IFormComponentProps extends FormComponentProps {
  onSubmit: (
    update: boolean,
    data?: { email?: string; password?: string; loginEnabled?: boolean },
  ) => any;
  onClickResetPassword: (email: string) => any;
  onClickDelete: () => any;
  client: Client;
  updating?: boolean;
}

interface IFormComponentState {
  passwordEditing: boolean;
  emailEditing: boolean;
  loginEnabled: boolean;
}

class EditClientForm extends React.Component<
  IFormComponentProps,
  IFormComponentState
> {
  state = {
    passwordEditing: false,
    emailEditing: false,
    loginEnabled: false,
  };

  constructor(props: Readonly<IFormComponentProps>) {
    super(props);

    this.state = {
      passwordEditing: false,
      emailEditing: false,
      loginEnabled: props.client?.loginEnabled,
    };
  }

  handleSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    try {
      this.props.form.validateFields(['email', 'password'], (err, values) => {
        if (!err) {
          const { email, password } =
            sanitizeObject<{ email: string; password: string }>(values);

          try {
            // await createClient(email, password, volcanoClientId);
            this.props.onSubmit(true, {
              email: email,
              password: password,
              loginEnabled: this.state.loginEnabled,
            });
          } catch (error) {
            this.props.onSubmit(false);
          }
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  onClickEditEmail = () => {
    if (this.state.emailEditing) {
      this.setState({
        emailEditing: false,
      });
    } else {
      this.setState({
        emailEditing: true,
      });
    }
  };

  onClickEditPassword = () => {
    if (this.state.passwordEditing) {
      this.setState({
        passwordEditing: false,
      });
    } else {
      this.setState({
        passwordEditing: true,
      });
    }
  };

  computeIconType = (key: 'email' | 'password') => {
    if (key === 'password') {
      if (this.state.passwordEditing) {
        return 'check';
      }

      return 'edit';
    }

    if (key === 'email') {
      if (this.state.emailEditing) {
        return 'check';
      }

      return 'edit';
    }

    return 'edit';
  };

  validateEmail = async (
    rule: any,
    value: any,
    callback: (arg0?: string) => void,
  ) => {
    // @ts-ignore
    const { form } = this.props;

    const valid = /.+@.+\..+/.test(form.getFieldValue('email'));

    if (!valid) {
      callback('Por favor, utiliza un correo electrónico válido');
    } else {
      const signInMethods = await firebase
        .auth()
        .fetchSignInMethodsForEmail(form.getFieldValue('email'));

      const userExists = signInMethods.length > 0 ? true : false;

      if (userExists && value !== this.props.client?.email) {
        callback('Ya existe un cliente con ese email');
      } else {
        callback();
      }
    }
  };

  validatePassword = (
    rule: any,
    value: any,
    callback: { (arg0: string): void; (): void },
  ) => {
    // @ts-ignore
    const { form } = this.props;
    if (/\s/.test(value)) {
      callback('La contraseña no puede contener espacios');
    } else if (
      form.getFieldValue('password')?.length < 6 &&
      form.getFieldValue('password')?.length > 1
    ) {
      callback(
        'Por favor, utiliza una contraseña que contenga como mínimo 6 caracteres',
      );
    } else {
      callback();
    }
  };

  render() {
    // @ts-ignore
    const { getFieldDecorator } = this.props.form;

    return (
      <Form
        id={'edit-client-form'}
        onSubmit={this.handleSubmit}
        layout="vertical"
        hideRequiredMark
      >
        <Form.Item label="Correo electrónico en Volcano">
          {getFieldDecorator('volcanoEmail', {
            rules: [],
          })(
            <Input
              disabled={true}
              placeholder={this.props.client?.volcanoEmail}
            />,
          )}
        </Form.Item>

        <Form.Item label="Correo electrónico">
          {getFieldDecorator('email', {
            initialValue: this.props.client?.email,
            rules: [{ type: 'email', validator: this.validateEmail }],
          })(
            <Input
              disabled={!this.state.emailEditing}
              addonAfter={
                <LegacyIcon
                  onClick={this.onClickEditEmail}
                  type={this.computeIconType('email')}
                />
              }
              placeholder={this.props.client?.email}
            />,
          )}
        </Form.Item>

        <Form.Item label="Contraseña">
          {getFieldDecorator('password', {
            rules: [{ type: 'string', validator: this.validatePassword }],
          })(
            <Input
              disabled={!this.state.passwordEditing}
              placeholder={'*******'}
              addonAfter={
                <LegacyIcon
                  onClick={this.onClickEditPassword}
                  type={this.computeIconType('password')}
                />
              }
            />,
          )}
        </Form.Item>
        <Form.Item label={'Acceso a Web Panel'}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <Switch
              onClick={(checked) => {
                this.setState({
                  loginEnabled: checked,
                });
              }}
              checkedChildren="Sí"
              unCheckedChildren="No"
              defaultChecked={this.props.client?.loginEnabled}
            />
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
              }}
            >
              <a
                style={{ marginBottom: '1em' }}
                className="login-form-forgot"
                href=""
                onClick={(e) => {
                  e.preventDefault();
                  this.props.onClickResetPassword(this.props.client?.email); // TODO: Fetch firebase email
                }}
              >
                Resetear contraseña
              </a>
              <a className="login-form-delete" href="">
                <Popconfirm
                  placement={'topLeft'}
                  title="Seguro que quieres dar de baja este cliente?"
                  okText="Si"
                  cancelText="No"
                  onConfirm={this.props.onClickDelete}
                >
                  Eliminar cuenta en Web Panel
                </Popconfirm>
              </a>
            </div>
          </div>
        </Form.Item>
      </Form>
    );
  }
}

export const MyEditClientForm =
  Form.create<IFormComponentProps>()(EditClientForm);

interface IEditClientDrawerProps {
  visible: boolean;
  clientId: string;
  volcanoClientId: string;
  hideDrawer: (...args: any[]) => void;
}

const EditClientModalContainer: React.FC<IEditClientDrawerProps> = (
  props: IEditClientDrawerProps,
) => {
  const [updating, setUpdating] = useState(false);

  const { data, networkStatus } = useGetClientBasicInfoQuery({
    client: getClient(),
    variables: {
      id: props.clientId,
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    skip: !props.clientId,
  });

  const [deleteClient] = useDeleteClientMutation({
    client: getClient(),
  });

  const [updateClient] = useUpdateClientMutation({
    client: getClient(),
  });

  const onSubmit = async (
    update: boolean,
    data?: { email?: string; password?: string; loginEnabled: boolean },
  ) => {
    if (update && data) {
      setUpdating(true);
      try {
        message.loading({
          content: 'Actualizando cliente..',
          key: 'client_update',
        });
        const result = await updateClient({
          variables: {
            id: props.clientId,
            email: data.email,
            password: data.password,
            loginEnabled: data.loginEnabled,
          },
        });

        if (result.data.updateClient) {
          message.success({
            content: 'Hecho!',
            key: 'client_update',
          });
          setUpdating(false);
        } else {
          message.error({
            content: 'Error al actualizar cliente',
            key: 'client_update',
          });
        }
      } catch (e) {
        message.error({
          content: 'Error al actualizar cliente',
          key: 'client_update',
        });
      } finally {
        setUpdating(false);
        setTimeout(() => {
          message.destroy();
        }, 3000);
      }
    }
    props.hideDrawer();
  };

  const onDelete = async () => {
    try {
      message.loading(
        {
          content: 'Eliminando cuenta..',
          key: 'client_delete',
        },
        0,
      );
      await deleteClient({
        variables: {
          id: props.clientId,
        },
      });
      message.success(
        {
          content: 'Cuenta eliminada correctamente',
          key: 'client_delete',
        },
        4,
      );
    } catch (e) {
      console.error(e);
      message.error(
        {
          content: 'Ha habido un error',
          key: 'client_delete',
        },
        4,
      );
    } finally {
      setTimeout(() => {
        message.destroy();
      }, 3000);
    }
    props.hideDrawer();
  };

  const client = data?.client;

  const sendPasswordResetEmail = async (email: string) => {
    try {
      message.loading(
        {
          content: 'Enviando email para resetear contraseña..',
          key: 'client_reset_pwd',
        },
        0,
      );

      await firebase.auth().sendPasswordResetEmail(email);

      message.success(
        {
          content: 'Email enviado',
          key: 'client_reset_pwd',
        },
        4,
      );
    } catch (e) {
      console.error(e);
      message.error(
        {
          content: 'Ha habido un error',
          key: 'client_reset_pwd',
        },
        4,
      );
    } finally {
      setTimeout(() => {
        message.destroy();
      }, 3000);
    }
  };

  const loading = networkStatus === 1 || networkStatus === 2;
  const content = loading ? (
    <Skeleton active={true} loading={true} />
  ) : (
    <MyEditClientForm
      updating={updating}
      onClickDelete={onDelete}
      onSubmit={onSubmit}
      //@ts-ignore
      client={client}
      onClickResetPassword={sendPasswordResetEmail}
    />
  );
  return (
    <div>
      <Modal
        footer={[
          <Button onClick={() => props.hideDrawer()}>Cerrar</Button>,
          <Button
            loading={updating}
            form="edit-client-form"
            key="submit"
            htmlType="submit"
            type={'primary'}
          >
            Guardar cambios
          </Button>,
        ]}
        onCancel={props.hideDrawer}
        // okText={'Guardar cambios'}
        // cancelText={'Cerrar sin guardar'}
        maskClosable={true}
        title={`Gestionar cliente ${props.volcanoClientId}`}
        width={500}
        destroyOnClose={true}
        closable={true}
        centered={true}
        // onClose={props.hideDrawer}
        visible={props.visible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        {content}
      </Modal>
    </div>
  );
};

const EditClientModal = (props: IEditClientDrawerProps) => {
  return <EditClientModalContainer {...props} />;
};

export default EditClientModal;
